import { useMemo, useState, useEffect, useCallback } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { FULL_DEAL_MAPPING, FULL_OUTPUT_MAPPING } from "@/lib/excel/mapping";
import { useDealExcelWorkbook } from "@/hooks/useExcelWorkbook";
import { TemplateUploader } from "@/components/admin/ExcelTemplate/TemplateUploader";
import { AssumptionsEditor } from "@/components/admin/ExcelTemplate/AssumptionsEditor";
import { ResultsPanel } from "@/components/admin/ExcelTemplate/ResultsPanel";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Download, Save, RefreshCw, Bug, Loader2 } from "lucide-react";
import { templateService } from "@/lib/services/templateService";
import { TemplateSelector } from "@/components/admin/ExcelTemplate/TemplateSelector";
import { TemplateUploaderToSupabase } from "@/components/admin/ExcelTemplate/TemplateUploaderToSupabase";
import { Badge } from "@/components/ui/badge";

const AdminExcelTemplate = () => {
  const mapping = useMemo(() => FULL_DEAL_MAPPING, []);
  const outputs = useMemo(() => FULL_OUTPUT_MAPPING, []);
  const [tab, setTab] = useState<"assumptions" | "results">("assumptions");
  const [activeTemplate, setActiveTemplate] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [supabaseTab, setSupabaseTab] = useState<"upload" | "select">("upload");
  const [debug, setDebug] = useState<any>({});
  const [isSaving, setIsSaving] = useState(false);

  const {
    fileName,
    isLoaded,
    dealData,
    loadFromFile,
    loadFromDeal,
    getInputsSnapshot,
    setInputs,
    getOutputsSnapshot,
    exportWorkbook,
    getWorkbook,
  } = useDealExcelWorkbook({ mapping, outputs });

  useEffect(() => {
    if (isLoaded) {
      const inputs = getInputsSnapshot();
      const outputs = getOutputsSnapshot();
      setDebug({ inputs, outputs, fileName });

    }
  }, [isLoaded, getInputsSnapshot, getOutputsSnapshot, fileName]);

  useEffect(() => {
    const loadActiveTemplate = async () => {
      try {
        setLoading(true);
        const template = await templateService.getActiveTemplate();

        if (template) {
          setActiveTemplate(template);

          try {
            const fileData = await templateService.getTemplateFile(template);
            const file = new File([fileData.data], fileData.fileName, {
              type: template.mime_type,
            });
            await loadFromFile(file);
            toast.success(`Loaded template: ${template.name}`);
          } catch (fileError) {
            console.error("Error loading template file:", fileError);
          }
        }
      } catch (error) {
        console.error("Error loading template:", error);
      } finally {
        setLoading(false);
      }
    };

    loadActiveTemplate();
  }, [loadFromFile]);

  const handleSaveToSupabase = useCallback(async () => {
    if (!isLoaded || !fileName) {
      toast.error("Please upload a template first");
      return;
    }

    setIsSaving(true);

    try {
      const wb = getWorkbook();
      if (!wb) return;

      const buffer = await wb.xlsx.writeBuffer();
      const file = new File([buffer], fileName, {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      await templateService.saveTemplate({
        name: activeTemplate?.name || "New Template",
        description: "Uploaded via interface",
        file,
        mapping: FULL_DEAL_MAPPING,
        outputs: FULL_OUTPUT_MAPPING,
      });

      toast.success("Template saved to Supabase with storage backup!");

      const updated = await templateService.getActiveTemplate();
      setActiveTemplate(updated);
    } catch (error) {
      console.error("Error saving to Supabase:", error);
      toast.error("Error saving to Supabase");
    } finally {
      setIsSaving(false);
    }
  }, [isLoaded, fileName, activeTemplate, getWorkbook]);

  const handleLoadDemoData = useCallback(async () => {
    try {
      const demoDeal = {
        name: "Malgart de Mar",
        location: "Spain",
        currency: "EUR",
        kpis: {
          propertyType: "Resort",
          stars: 4,
          gfaSqm: 5072,
          rooms: { total: 54 },
          roomMix: [
            { name: "Standard", count: 52 },
            { name: "Studio", count: 2 },
          ],
        },
      };

      if (fileName) {
        const response = await fetch(fileName);
        const blob = await response.blob();
        const file = new File([blob], fileName);
        await loadFromDeal(demoDeal, file);
        toast.success("Demo data loaded");
      }
    } catch (error) {
      console.error("Error loading demo data:", error);
      toast.error("Error loading demo data");
    }
  }, [fileName, loadFromDeal]);

  const handleTemplateSelect = async (template: any) => {
    try {
      setLoading(true);

      const fileData = await templateService.getTemplateFile(template);
      const file = new File([fileData.data], fileData.fileName, {
        type: template.mime_type,
      });

      await loadFromFile(file);
      setActiveTemplate(template);
      toast.success(`Loaded template: ${template.name}`);
    } catch (error) {
      console.error("Error loading template:", error);
      toast.error("Error loading template");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <main className="mx-auto w-full px-4">
        <div className="grid gap-6">
          <Card>
            <CardHeader>
              <CardTitle>Supabase Template Management</CardTitle>
              <CardDescription>
                Upload a template to the database or select an existing one
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Tabs
                value={supabaseTab}
                onValueChange={(v) => setSupabaseTab(v as any)}
              >
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="upload">Upload New</TabsTrigger>
                  <TabsTrigger value="select">Select from Database</TabsTrigger>
                </TabsList>

                <TabsContent value="upload" className="mt-4">
                  <TemplateUploaderToSupabase
                    onSuccess={() => {
                      setSupabaseTab("select");
                      toast.success("Template saved successfully");
                    }}
                  />
                </TabsContent>

                <TabsContent value="select" className="mt-4">
                  <TemplateSelector onSelect={handleTemplateSelect} />
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>1) Upload Excel Template</CardTitle>
                <CardDescription>
                  File stays in your browser. All mapped fields will sync with
                  deal data.
                </CardDescription>
              </div>
              <div className="flex items-center gap-2">
                {activeTemplate && (
                  <span className="text-sm text-green-600 bg-green-50 px-3 py-1 rounded-full">
                    Active: {activeTemplate.name} v{activeTemplate.version}
                  </span>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleSaveToSupabase}
                  disabled={!isLoaded || isSaving}
                >
                  {isSaving ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4 mr-2" />
                  )}
                  {isSaving ? "Saving..." : "Save to Supabase"}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleLoadDemoData}
                  disabled={!isLoaded || isSaving}
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Load Demo Data
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <TemplateUploader onFileSelected={loadFromFile} />

              {fileName && (
                <div className="mt-4 p-3 bg-blue-50 rounded-md">
                  <p className="text-sm text-blue-700">
                    <span className="font-medium">Uploaded file:</span>{" "}
                    {fileName}
                  </p>
                  {dealData && (
                    <p className="text-sm text-green-700 mt-1">
                      <span className="font-medium">Deal loaded:</span>{" "}
                      {dealData.name}
                    </p>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>2) Edit Data</CardTitle>
              <CardDescription>
                Modify values and download the updated file
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Tabs value={tab} onValueChange={(v) => setTab(v as any)}>
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="assumptions" disabled={!isLoaded}>
                    Parameters ({Object.keys(getInputsSnapshot()).length})
                  </TabsTrigger>
                  <TabsTrigger value="results" disabled={!isLoaded}>
                    Results ({getOutputsSnapshot().length})
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="assumptions">
                  <AssumptionsEditor
                    disabled={!isLoaded || loading}
                    mapping={mapping}
                    initialValues={getInputsSnapshot()}
                    onApply={async (next) => {
                      await setInputs(next);
                      setTab("results");
                      toast.success("Changes applied successfully");
                    }}
                    onExport={exportWorkbook}
                  />
                </TabsContent>

                <TabsContent value="results">
                  <ResultsPanel
                    disabled={!isLoaded || loading}
                    outputs={getOutputsSnapshot()}
                  />

                  {isLoaded && (
                    <div className="mt-6 flex justify-end">
                      <Button
                        onClick={exportWorkbook}
                        className="bg-primary-blue"
                      >
                        <Download className="w-4 h-4 mr-2" />
                        Download Updated Excel
                      </Button>
                    </div>
                  )}
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>

          {debug.inputs && Object.keys(debug.inputs).length > 0 && (
            <Card className="border-yellow-300 shadow-lg">
              <CardHeader className="bg-gradient-to-r from-yellow-50 to-amber-50 border-b border-yellow-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="p-2 bg-yellow-100 rounded-lg">
                      <Bug className="w-5 h-5 text-yellow-700" />
                    </div>
                    <div>
                      <CardTitle className="text-yellow-800">
                        Debug Information Panel
                      </CardTitle>
                      <CardDescription className="text-yellow-600">
                        Real-time data synchronization status between Excel and
                        JSON
                      </CardDescription>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Badge
                      variant="outline"
                      className="bg-green-50 text-green-700 border-green-200"
                    >
                      🟢 Live
                    </Badge>
                  </div>
                </div>
              </CardHeader>

              <CardContent className="pt-6">
                <div className="grid grid-cols-4 gap-4 mb-6">
                  <div className="bg-blue-50 p-4 rounded-lg border border-blue-100">
                    <div className="text-sm text-blue-600 font-medium">
                      Total Parameters
                    </div>
                    <div className="text-2xl font-bold text-blue-800">
                      {Object.keys(debug.inputs).length}
                    </div>
                    <div className="text-xs text-blue-500 mt-1">
                      from Excel mapping
                    </div>
                  </div>

                  <div className="bg-green-50 p-4 rounded-lg border border-green-100">
                    <div className="text-sm text-green-600 font-medium">
                      Output Results
                    </div>
                    <div className="text-2xl font-bold text-green-800">
                      {debug.outputs?.length || 0}
                    </div>
                    <div className="text-xs text-green-500 mt-1">
                      calculated values
                    </div>
                  </div>

                  <div className="bg-purple-50 p-4 rounded-lg border border-purple-100">
                    <div className="text-sm text-purple-600 font-medium">
                      Success Rate
                    </div>
                    <div className="text-2xl font-bold text-purple-800">
                      {mapping.length > 0
                        ? (
                            (Object.keys(debug.inputs).length /
                              mapping.length) *
                            100
                          ).toFixed(1)
                        : "0"}
                      %
                    </div>
                    <div className="text-xs text-purple-500 mt-1">
                      of {mapping.length} mapped fields
                    </div>
                  </div>

                  <div className="bg-amber-50 p-4 rounded-lg border border-amber-100">
                    <div className="text-sm text-amber-600 font-medium">
                      File
                    </div>
                    <div className="text-sm font-bold text-amber-800 mt-1">
                      {fileName || "No file"}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </main>
    </div>
  );
};

export default AdminExcelTemplate;
